import React, { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import Text from "@amzn/meridian/text";
import Column from "@amzn/meridian/column";
import Alert from "@amzn/meridian/alert";
import { ActionBar, AppealsTabNavigation } from "./action-bar/ActionBar";
import { ApproachComponent } from "./approach-table/ApproachComponent";
import { CostApproachComponent } from "./cost-approach/CostApproachComponent";
import { IncomeApproachComponent } from "./income-approach/IncomeApproachComponent";
import { AppealsSummary } from "./summary/AppealsSummary";
import { useUpdateAppealMutation } from "../../endpoints/updateAppeal";
import { useAppSelector } from "../../app/store/hooks";
import ApiIndicator from "../../features/api-indicator/ApiIndicator";
import { getErrorMessage } from "../../features/api-indicator/utils";
import {
    useGetAppealDetailsQuery,
    useLazyCreateAppealQuery,
} from "../../endpoints/getAppealDetails";
import { StatusEnum } from "../../endpoints/getArtifacts";
import { usePreventWindowUnload } from "./hooks";

interface AppealsComponentProps {
    propertyId: string;
    isValidated: boolean;
    missingFields: string[];
}

export const AppealsComponent: React.FC<AppealsComponentProps> = ({
    propertyId,
    isValidated,
    missingFields,
}) => {
    const { alias } = useAppSelector(state => state.auth.user);
    const [year, setYear] = useState(String(new Date().getFullYear()));
    const [isEditing, setIsEditing] = useState(false);
    const [data, setData] = useState<any>(null);
    const [propertyType, setPropertyType] = useState<keyof typeof AppealsTabNavigation>(
        AppealsTabNavigation.SUMMARY
    );

    const {
        data: appealData,
        isFetching,
        isLoading,
        isSuccess,
        error,
        isError,
        isUninitialized,
        refetch: getAppealRefetch,
    } = useGetAppealDetailsQuery(
        {
            propertyArtifactId: String(propertyId),
            year: String(year),
            businessUseCase: "SMART",
        },
        {
            skip: !isValidated,
        }
    );
    const [createAppeal] = useLazyCreateAppealQuery();
    const [updateAppealDetails, { isError: appealError, error: appealErrorMsg }] =
        useUpdateAppealMutation();

    const [loading, setLoading] = useState(false);
    const [isAppealStatusLoading, setIsAppealStatusLoading] = useState(false);

    useEffect(() => {
        if (appealData) {
            setData(JSON.parse(JSON.stringify(appealData)));
        }
    }, [appealData]);

    const handleCreateAppeal = async () => {
        setIsAppealStatusLoading(true);
        await createAppeal({
            propertyArtifactId: String(propertyId),
            year: String(year),
            businessUseCase: "SMART",
            clientName: alias,
        });
        setIsAppealStatusLoading(false);
        window.location.reload();
    };

    const handleUpdateData = (updatedData: any) => {
        setData((prevData: any) => ({ ...prevData, ...updatedData }));
    };

    const updateData = async (updatedData: any) => {
        setLoading(true);
        if ("editingUser" in updatedData) {
            delete updatedData.editingUser;
        }
        await updateAppealDetails({
            businessUseCase: "SMART",
            appealArtifactId: data.appealArtifactId,
            appealDetails: updatedData,
            appealStatus: data.appealDetails.status,
            clientName: alias,
        });
        await getAppealRefetch();
        setLoading(false);
    };
    const appealsRef = useRef(null);
    const handleBeforeUnload = useCallback(() => {
        updateAppealDetails({
            businessUseCase: "SMART",
            appealArtifactId: appealsRef.current.data.appealArtifactId,
            appealDetails: {},
            appealStatus: StatusEnum.PREPARING,
            clientName: alias,
        });
        window.history.replaceState({ blockNavigation: false }, "");
    }, []);

    usePreventWindowUnload(appealsRef, handleBeforeUnload);

    useEffect(() => {
        const currentUserIsEditing =
            data?.appealDetails?.status === StatusEnum.EDITING.toString() &&
            data?.appealDetails?.editingUser === alias;
        appealsRef.current = {
            preventUnload: currentUserIsEditing,
            blockNavigation: currentUserIsEditing,
            data: data,
        };
        window.history.replaceState({ blockNavigation: currentUserIsEditing }, "");
    }, [isEditing, data]);

    const isDataReady = !isFetching && !isLoading && !loading;
    const showContent = isValidated && isDataReady && !isError && isSuccess && data;

    const renderApproachComponent = () => {
        const components = {
            [AppealsTabNavigation.SUMMARY]: () => (
                <AppealsSummary
                    year={year}
                    data={data}
                    handleUpdateData={handleUpdateData}
                    isEditing={isEditing}
                    updateData={updateData}
                />
            ),
            [AppealsTabNavigation.COST]: () => (
                <CostApproachComponent
                    data={data}
                    handleUpdateData={handleUpdateData}
                    isEditing={isEditing}
                    viewType="COST"
                />
            ),
            [AppealsTabNavigation.INCOME]: () => (
                <IncomeApproachComponent
                    data={data}
                    handleUpdateData={handleUpdateData}
                    isEditing={isEditing}
                    viewType="INCOME"
                    refetch={getAppealRefetch}
                />
            ),
            [AppealsTabNavigation.SALES]: () => (
                <ApproachComponent
                    data={data}
                    handleUpdateData={handleUpdateData}
                    isEditing={isEditing}
                    viewType="SALES"
                    refetch={getAppealRefetch}
                />
            ),
        };

        return data.status === "IN_PROGRESS" ? (
            <Text>In Progress</Text>
        ) : (
            components[propertyType]?.() || null
        );
    };

    if (!isValidated) {
        return (
            <Alert type="warning">
                <Text type="h200">Some attribute values are missing for this property.</Text>
                <Text type="b300">
                    Calculation can only be done after these values are provided.
                </Text>
                <Text type="b300">Missing Fields: {missingFields.join(", ")}</Text>
            </Alert>
        );
    }

    if (!isDataReady) {
        return (
            <ApiIndicator
                isUninitialized={isUninitialized}
                isLoading={isLoading || loading}
                isError={isError}
                error={error}
                isToastError={true}
                isFetching={isFetching}
                loaderType="linear"
                timeout={3000}
            />
        );
    }

    return (
        <Column width="fit" overflowX="hidden">
            <Column width="fill" minWidth="950px">
                <ActionBar
                    propertyType={propertyType}
                    setPropertyType={setPropertyType}
                    year={year}
                    setYear={setYear}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    updateData={updateData}
                    isError={isError}
                    errorMessage={getErrorMessage(error)}
                    handleCreateAppeal={handleCreateAppeal}
                    isAppealStatusLoading={isAppealStatusLoading}
                    data={data}
                    appealUpdateError={appealError}
                    appealUpdateErrorMessage={appealErrorMsg}
                />
                {showContent && renderContent()}
            </Column>
        </Column>
    );

    function renderContent() {
        if (data.status === "ERROR" || data.status === "FAILED") {
            return renderErrorAlert();
        }
        if (data.status === "IN_PROGRESS" || data.status === "REGISTERED") {
            return renderProgressAlert();
        }

        return renderApproachComponent();
    }

    function renderErrorAlert() {
        const errorMessages = Object.entries(
            _.get(data, "appealDetails.appealApproachCalculationData", {})
        )
            .filter(
                ([, value]) =>
                    typeof value === "object" && value !== null && "errorMessage" in value
            )
            .map(([key, value]) => {
                const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);

                return `${capitalizedKey} Approach: ${(value as { errorMessage: string }).errorMessage}`;
            })
            .join(". ");

        return (
            <Alert type="error">
                <Text type="h100">
                    The system failed to calculate and generate the appeals package.
                </Text>
                <Text type="b300">{errorMessages}</Text>
            </Alert>
        );
    }

    function renderProgressAlert() {
        return (
            <Alert type="theme">
                <Text type="h100">
                    The system is calculating and generating the appeals package.
                </Text>
                <Text type="b300">
                    Please come back in a few minutes to view and edit the appeals package.
                </Text>
            </Alert>
        );
    }
};
